<template>
  <div
    class="weekly-activity-assignments-table weekly-activity-assignments-table--takeoff-mode"
    :class="{ 'weekly-activity-assignments-table--reschedule': rescheduleMode }"
  >
    <div v-if="showToolbar && !rescheduleMode" class="weekly-activity-assignments-table__toolbar">
      <div class="flex items-center gap-2 flex-wrap">
        <span v-if="level != null" class="weekly-activity-assignments-table__level-badge">
          {{ $t('weekly_assignments_level', { level }) }}
        </span>
        <span v-if="!showSectionHeader" class="weekly-activity-assignments-table__count">
          {{ displayCount }} {{ $t(countLabelKey) }}
        </span>
      </div>
    </div>

    <div class="weekly-activity-assignments-table__body">
      <slot>
        <div
          class="weekly-activity-assignments-table__section"
          :class="{ 'weekly-activity-assignments-table__section--nested': !showSectionHeader }"
        >
          <div
            v-if="showSectionHeader"
            class="weekly-activity-assignments-table__section-header"
            :class="{ 'weekly-activity-assignments-table__section-header--takeoff': isTakeoffRootView }"
          >
            <b-icon
              pack="fas"
              icon="tags"
              size="is-small"
              class="weekly-activity-assignments-table__section-icon"
            />
            <span class="weekly-activity-assignments-table__section-title">
              {{ $t('weekly_assignments_takeoff_title', { count: displayCount }) }}
            </span>
            <span
              v-if="rescheduleMode"
              class="weekly-activity-assignments-table__section-badge"
            >
              TAKEOFF
            </span>
          </div>

          <div
            v-if="isTakeoffRootView && !isReschedulePlanStep"
            class="weekly-activity-assignments-table__takeoff-filters"
          >
            <div
              v-for="def in takeoffFilterDefs"
              :key="def.code"
              class="weekly-activity-assignments-table__takeoff-filter"
            >
              <span class="weekly-activity-assignments-table__takeoff-filter-label">
                {{ $t(def.labelKey) }}
              </span>
              <b-autocomplete
                v-model="takeoffFilterQueries[def.code]"
                :data="filteredTakeoffOptions(def.code)"
                :placeholder="$t('all')"
                open-on-focus
                clearable
                append-to-body
                class="weekly-activity-assignments-table__takeoff-autocomplete"
                @typing="(value) => onTakeoffFilterTyping(def.code, value)"
                @select="(option) => onTakeoffFilterSelect(def.code, option)"
              >
                <template #empty>{{ $t('no_results') }}</template>
              </b-autocomplete>
            </div>
            <span
              v-if="rescheduleMode"
              class="weekly-activity-assignments-table__filter-count"
            >
              {{ displayRows.length }}/{{ displayCount || displayRows.length }}
            </span>
          </div>

          <div
            v-if="rescheduleMode && isTakeoffRootView"
            class="weekly-activity-assignments-table__reschedule-step"
          >
            <template v-if="!isReschedulePlanStep">
              <span class="weekly-activity-assignments-table__reschedule-step-text">
                {{ $t('step') }} 1 · {{ $t('weekly_takeoff_reschedule_select_tags') }}
              </span>
              <button
                type="button"
                class="weekly-activity-assignments-table__reschedule-confirm"
                :disabled="!rescheduleSelectedCount"
                @click="confirmRescheduleSelection"
              >
                <b-icon pack="fas" icon="check" size="is-small" />
                <span>{{ $t('weekly_confirm_selection', { count: rescheduleSelectedCount }) }}</span>
              </button>
            </template>
            <template v-else>
              <span class="weekly-activity-assignments-table__reschedule-step-text">
                {{ $t('step') }} 2 · {{ $t('weekly_takeoff_reschedule_plan_weeks') }}
              </span>
              <button
                type="button"
                class="weekly-activity-assignments-table__reschedule-back"
                @click="backToRescheduleSelection"
              >
                <b-icon pack="fas" icon="arrow-left" size="is-small" />
                <span>{{ $t('back') }}</span>
              </button>
              <button
                type="button"
                class="weekly-activity-assignments-table__reschedule-confirm"
                :disabled="!canSaveRescheduleWeekDrafts || savingRescheduleWeekDrafts"
                @click="saveRescheduleWeekDrafts"
              >
                <b-icon
                  pack="fas"
                  :icon="savingRescheduleWeekDrafts ? 'spinner' : 'save'"
                  size="is-small"
                  :custom-class="savingRescheduleWeekDrafts ? 'fa-spin' : null"
                />
                <span>{{ $t('save') }}</span>
              </button>
            </template>
          </div>

          <div
            class="weekly-activity-assignments-table__grid-wrap"
            :class="{ 'weekly-activity-assignments-table__grid-wrap--compact': rescheduleMode }"
          >
          <table
            class="weekly-activity-assignments-table__grid text-xs border-collapse"
            :class="rescheduleMode ? 'weekly-activity-assignments-table__grid--compact' : 'w-full min-w-max'"
          >
            <thead>
              <tr>
                <th
                  v-if="!isReschedulePlanStep"
                  class="weekly-activity-assignments-table__header weekly-activity-assignments-table__expand-col"
                />
                <th
                  v-if="rescheduleMode && !isReschedulePlanStep"
                  class="weekly-activity-assignments-table__header weekly-activity-assignments-table__check-col"
                />
                <template v-if="!rescheduleMode">
                  <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__actions-col" />
                  <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_table_slot') }}</th>
                  <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__header--left">{{ $t('tag') }}</th>
                  <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__header--left">{{ $t('stage') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_scheduled_quantity') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('weekly_assignment_executed_quantity') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('ud') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('weight') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('progress_percent') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('mh_index') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('scheduled_hours') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('registered_hours') }}</th>
                </template>
                <template v-else-if="isReschedulePlanStep">
                  <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__header--left">{{ $t('tag') }}</th>
                  <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__header--left">{{ $t('stage') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('pending') }}</th>
                  <th
                    v-for="weekNum in rescheduleWeekNumbers"
                    :key="`reschedule-week-h-${weekNum}`"
                    class="weekly-activity-assignments-table__header weekly-activity-assignments-table__header--week"
                  >
                    S{{ weekNum }}
                  </th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('supervisor') }}</th>
                </template>
                <template v-else>
                  <th class="weekly-activity-assignments-table__header weekly-activity-assignments-table__header--left">{{ $t('tag') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('weight') }}</th>
                  <th class="weekly-activity-assignments-table__header">{{ $t('progress_percent') }}</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loadingData && !displayRows.length">
                <td :colspan="tableColspan" class="weekly-activity-assignments-table__cell px-4 py-6 text-center text-gray-500">
                  <b-loading :active="true" :is-full-page="false" />
                </td>
              </tr>
              <tr v-else-if="!displayRows.length">
                <td :colspan="tableColspan" class="weekly-activity-assignments-table__cell px-4 py-6 text-center text-gray-500">
                  {{ $t('no_results') }}
                </td>
              </tr>
              <template v-for="(assignment, assignmentIndex) in displayRows">
                <tr
                  :key="assignmentRowKey(assignment, assignmentIndex) + '-row'"
                  class="hover:bg-green-lightest"
                  :class="{
                    'weekly-activity-assignments-table__row--expanded': isAssignmentExpanded(assignment, assignmentIndex),
                    'weekly-activity-assignments-table__row--imported': isImportedBoqSource(assignment),
                    'weekly-activity-assignments-table__row--selected': rescheduleMode && isRescheduleSelected(assignment),
                  }"
                >
                  <td
                    v-if="!isReschedulePlanStep"
                    class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__expand-col text-center"
                  >
                    <button
                      v-if="assignmentHasChildren(assignment)"
                      type="button"
                      class="inline-flex items-center justify-center w-7 h-7 text-green-light hover:text-green focus:outline-none"
                      :aria-expanded="isAssignmentExpanded(assignment, assignmentIndex)"
                      :aria-label="isAssignmentExpanded(assignment, assignmentIndex)
                        ? $t('weekly_assignment_hide_sub_measurements')
                        : $t('weekly_assignment_show_sub_measurements')"
                      @click="toggleAssignmentDetail(assignment, assignmentIndex)"
                    >
                      <b-icon
                        pack="fas"
                        :icon="isAssignmentExpanded(assignment, assignmentIndex) ? 'chevron-down' : 'chevron-right'"
                        size="is-small"
                      />
                    </button>
                  </td>

                  <td
                    v-if="rescheduleMode && !isReschedulePlanStep"
                    class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__check-col text-center"
                  >
                    <label
                      v-if="canSelectForReschedule(assignment)"
                      class="weekly-activity-assignments-table__check"
                      @click.stop
                    >
                      <input
                        type="checkbox"
                        :checked="isRescheduleSelected(assignment)"
                        @change="toggleRescheduleSelection(assignment)"
                      >
                      <span
                        class="weekly-activity-assignments-table__check-mark"
                        :class="{
                          'weekly-activity-assignments-table__check-mark--on': isRescheduleSelected(assignment),
                          'weekly-activity-assignments-table__check-mark--done': assignment.completed,
                        }"
                      >
                        <b-icon
                          v-if="isRescheduleSelected(assignment) || assignment.completed"
                          pack="fas"
                          icon="check"
                          size="is-small"
                        />
                      </span>
                    </label>
                  </td>

                  <template v-if="!rescheduleMode">
                    <!-- Acciones -->
                    <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__actions-col text-center">
                      <div class="text-center cursor-pointer" @click.stop>
                        <b-dropdown aria-role="list" position="is-bottom-right" append-to-body>
                          <template #trigger="{ active }">
                            <button
                              type="button"
                              class="inline-flex items-center justify-center w-7 h-7 text-grey-dark hover:text-green focus:outline-none"
                              :aria-label="$t('actions')"
                            >
                              <b-icon pack="fas" :icon="active ? 'ellipsis-h' : 'ellipsis-v'" size="is-small" />
                            </button>
                          </template>
                          <b-dropdown-item
                            v-if="canRegisterProgress(assignment)"
                            aria-role="listitem"
                            @click="onRegisterProgressClick(assignment)"
                          >
                            <span class="text-xs">{{ $t('register_progress') }}</span>
                          </b-dropdown-item>
                        </b-dropdown>
                      </div>
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <div class="weekly-activity-assignments-table__slot">
                        <b-icon
                          v-if="hasBoqRootSource(assignment)"
                          pack="fas"
                          :icon="assignmentSlotIcon(assignment)"
                          size="is-small"
                          class="weekly-activity-assignments-table__slot-icon"
                          :class="assignmentSlotIconClass(assignment)"
                        />
                        <span
                          class="weekly-activity-assignments-table__slot-badge"
                          :class="assignmentSlotBadgeClass(assignment)"
                        >
                          {{ assignmentTypeLabel(assignment) }}
                        </span>
                      </div>
                    </td>
                    <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__tag-cell">
                      <div class="weekly-activity-assignments-table__tag-main">
                        <b-icon pack="fas" icon="tag" size="is-small" class="weekly-activity-assignments-table__tag-icon" />
                        <div class="weekly-activity-assignments-table__tag-texts">
                          <span v-if="isEmpty(assignment.code)" class="weekly-activity-assignments-table__empty">—</span>
                          <a v-else href="#" class="weekly-activity-assignments-table__code-link" @click.prevent>
                            {{ assignment.code }}
                          </a>
                          <span class="weekly-activity-assignments-table__tag-sub">
                            {{ assignmentDescription(assignment) }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td class="weekly-activity-assignments-table__cell whitespace-nowrap">
                      {{ assignmentStageName(assignment) }}
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span v-if="isEmpty(assignmentTotal(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                      <span v-else>{{ formatQuantity(assignmentTotal(assignment)) }}</span>
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span v-if="isEmpty(assignmentExecutedQuantity(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                      <span v-else>{{ formatQuantity(assignmentExecutedQuantity(assignment)) }}</span>
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      {{ assignmentUnit(assignment) }}
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span class="weekly-activity-assignments-table__weight-value">
                        {{ formatWeightPercent(assignment) }}
                      </span>
                    </td>
                    <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__progress-cell">
                      <div
                        v-if="isTakeoffProgressEditing(assignment)"
                        class="weekly-activity-assignments-table__progress-edit"
                        @click.stop
                      >
                        <input
                          v-model.number="takeoffProgressEdit.percent"
                          type="number"
                          min="0"
                          max="100"
                          step="0.00001"
                          class="weekly-activity-assignments-table__progress-input"
                          :disabled="takeoffProgressEdit.saving"
                        />
                        <span class="weekly-activity-assignments-table__progress-suffix">%</span>
                        <button
                          type="button"
                          class="weekly-activity-assignments-table__progress-action weekly-activity-assignments-table__progress-action--save"
                          :disabled="takeoffProgressEdit.saving"
                          :title="$t('save')"
                          :aria-label="$t('save')"
                          @click="saveTakeoffProgressEdit(assignment)"
                        >
                          <b-icon pack="fas" icon="check" size="is-small" />
                        </button>
                        <button
                          type="button"
                          class="weekly-activity-assignments-table__progress-action weekly-activity-assignments-table__progress-action--cancel"
                          :disabled="takeoffProgressEdit.saving"
                          :title="$t('cancel')"
                          :aria-label="$t('cancel')"
                          @click="cancelTakeoffProgressEdit"
                        >
                          <b-icon pack="fas" icon="times" size="is-small" />
                        </button>
                      </div>
                      <progress-bar
                        v-else
                        :step="assignmentProgress(assignment)"
                        color="green-light"
                        class="w-full"
                      />
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span v-if="isEmpty(assignmentAdjustedManHourIndex(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                      <span v-else>{{ formatQuantity(assignmentAdjustedManHourIndex(assignment)) }}</span>
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span v-if="isEmpty(assignmentScheduledHours(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                      <span v-else>{{ formatQuantity(assignmentScheduledHours(assignment)) }}</span>
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span v-if="isEmpty(assignmentRegisteredHours(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                      <span v-else>{{ formatQuantity(assignmentRegisteredHours(assignment)) }}</span>
                    </td>
                  </template>

                  <template v-else-if="isReschedulePlanStep">
                    <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__tag-cell">
                      <div class="weekly-activity-assignments-table__tag-main">
                        <b-icon pack="fas" icon="tag" size="is-small" class="weekly-activity-assignments-table__tag-icon" />
                        <div class="weekly-activity-assignments-table__tag-texts">
                          <span v-if="isEmpty(assignment.code)" class="weekly-activity-assignments-table__empty">—</span>
                          <a v-else href="#" class="weekly-activity-assignments-table__code-link" @click.prevent>
                            {{ assignment.code }}
                          </a>
                          <span class="weekly-activity-assignments-table__tag-sub">
                            {{ assignmentDescription(assignment) }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td class="weekly-activity-assignments-table__cell whitespace-nowrap">
                      {{ assignmentStageName(assignment) }}
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span v-if="isEmpty(assignmentPending(assignment))" class="weekly-activity-assignments-table__empty">—</span>
                      <span v-else>{{ formatQuantity(assignmentPending(assignment)) }}</span>
                    </td>
                    <td
                      v-for="weekNum in rescheduleWeekNumbers"
                      :key="`${assignmentRowKey(assignment, assignmentIndex)}-week-${weekNum}`"
                      class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__week-cell text-center"
                    >
                      <input
                        v-model.number="assignment.weekDrafts[weekNum]"
                        type="number"
                        min="0"
                        step="0.00001"
                        class="weekly-activity-assignments-table__week-input"
                        @input="emitRescheduleWeekDraftsPreview"
                      >
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      {{ assignmentSupervisor(assignment) }}
                    </td>
                  </template>

                  <template v-else>
                    <td class="weekly-activity-assignments-table__cell weekly-activity-assignments-table__tag-cell">
                      <div class="weekly-activity-assignments-table__tag-main">
                        <b-icon pack="fas" icon="tag" size="is-small" class="weekly-activity-assignments-table__tag-icon" />
                        <div class="weekly-activity-assignments-table__tag-texts">
                          <span v-if="isEmpty(assignment.code)" class="weekly-activity-assignments-table__empty">—</span>
                          <a v-else href="#" class="weekly-activity-assignments-table__code-link" @click.prevent>
                            {{ assignment.code }}
                          </a>
                          <span class="weekly-activity-assignments-table__tag-sub">
                            {{ assignmentDescription(assignment) }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span class="weekly-activity-assignments-table__weight-value">
                        {{ formatWeightPercent(assignment) }}
                      </span>
                    </td>
                    <td class="weekly-activity-assignments-table__cell text-center whitespace-nowrap">
                      <span class="weekly-activity-assignments-table__progress-value">
                        {{ formatProgressPercent(assignment) }}
                      </span>
                    </td>
                  </template>
                </tr>
                <tr
                  v-if="!isReschedulePlanStep && assignmentHasChildren(assignment) && isAssignmentExpanded(assignment, assignmentIndex)"
                  :key="`${assignmentRowKey(assignment, assignmentIndex)}-detail`"
                  class="weekly-activity-assignments-table__detail-row"
                >
                  <td :colspan="tableColspan" class="p-0 weekly-activity-assignments-table__detail-cell">
                    <div class="weekly-activity-assignments-table__detail-panel">
                      <weekly-activity-assignments-table-takeoff
                        :show-toolbar="false"
                        :activity-id="activityId"
                        :parent-assignment-id="assignment.id"
                        :takeoff-active-filters="takeoffFiltersForChildren"
                        :reschedule-mode="rescheduleMode"
                        :reschedule-selected-ids="activeRescheduleSelectedIds"
                        :selected-discipline-id="selectedDisciplineId"
                        @takeoff-filter-values="mergeTakeoffFilterOptions"
                        @toggle-reschedule-assignment="onToggleRescheduleAssignment"
                      />
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="loadingMore">
                <td :colspan="tableColspan" class="weekly-activity-assignments-table__cell px-4 py-3 text-center text-gray-500">
                  <b-icon pack="fas" icon="circle-notch" size="is-small" custom-class="fa-spin" />
                  <span class="text-xs">{{ $t('loading') }}… ({{ assignments.length }})</span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex'
import EventBus from '~/plugins/bus'
import {
  readWeeklyUiSession,
  writeWeeklyUiSession,
} from './weekly-ui-session.js'

const ASSIGNMENT_LINE_STATUS_OK = 0

/** Tamaño de lote para la carga progresiva de mediciones raíz. */
const ASSIGNMENTS_PAGE_SIZE = 10

/**
 * Variantes visibles en la vista takeoff:
 * type5 (14) = import tipo 1, type6 (15) = import tipo 2,
 * type2 (11) = mediciones importadas antes del cambio a type5.
 */
const TAKEOFF_VARIANT_TYPES = [11, 14, 15]

/** type5: progress ya viene como % de avance (0-100), no como unidades ejecutadas. */
const VARIANT_TYPE_TAKEOFF = 14

/** type6 (takeoff tipo 2): progress también es %; total padre en unidades. */
const VARIANT_TYPE_TAKEOFF_TYPE2 = 15

/** Cantidades y porcentajes se muestran con hasta 5 decimales, igual que el árbol BOQ. */
const QUANTITY_DECIMALS = 5

function formatDecimal(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return null
  }
  return number.toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: QUANTITY_DECIMALS,
  })
}

function roundDecimal(value) {
  const factor = 10 ** QUANTITY_DECIMALS
  return Math.round(Number(value) * factor) / factor
}

export default {
  name: 'weekly-activity-assignments-table-takeoff',

  props: {
    /** Actividad o subactividad: assignable_id de las mediciones (GET /api/v2/activity/{id}/assignments). */
    activityId: {
      type: [Number, String],
      default: null,
    },
    projectId: {
      type: Number,
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    /** Medición padre cuando se listan sub-mediciones (instancia recursiva). */
    parentAssignmentId: {
      type: [Number, String],
      default: null,
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
    level: {
      type: Number,
      default: null,
    },
    count: {
      type: Number,
      default: 0,
    },
    countLabelKey: {
      type: String,
      default: 'assignments',
    },
    /** Filtros takeoff activos heredados del padre (solo tablas anidadas). */
    takeoffActiveFilters: {
      type: Object,
      default: null,
    },
    /** Compacta columnas y permite seleccionar sub-mediciones (TAGS). */
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
    /** IDs seleccionados en reprogramación (heredados desde la raíz del árbol). */
    rescheduleSelectedIds: {
      type: Array,
      default: null,
    },
    /** Disciplina BoqRoot (null = Todas). Filtra padres/hijos por stages del subárbol. */
    selectedDisciplineId: {
      type: Number,
      default: null,
    },
  },

  emits: ['created', 'takeoff-filter-values', 'toggle-reschedule-assignment', 'confirm-reschedule-selection'],

  beforeCreate() {
    this.$options.components.WeeklyActivityAssignmentsTableTakeoff = this.$options
  },

  data() {
    return {
      assignments: [],
      loadingData: false,
      loadingMore: false,
      paginationAfterIds: null,
      hasMoreAssignments: false,
      /** Invalida lotes en vuelo cuando cambia actividad/disciplina o se recarga. */
      assignmentsLoadToken: 0,
      expandedAssignmentKeys: [],
      silentRefreshTimer: null,
      takeoffFilters: {
        area: '',
        engineering_doc: '',
        planner: '',
      },
      takeoffFilterQueries: {
        area: '',
        engineering_doc: '',
        planner: '',
      },
      /** Listas únicas precargadas de value_free por código de filtro. */
      takeoffFilterOptions: {
        area: [],
        engineering_doc: [],
        planner: [],
      },
      takeoffProgressEdit: {
        assignmentId: null,
        percent: null,
        originalPercent: null,
        saving: false,
      },
      localRescheduleSelectedIds: [],
      /** 1 = seleccionar TAGs; 2 = planificar semanas de seleccionados. */
      reschedulePlanStep: 1,
      confirmedRescheduleAssignments: [],
      savingRescheduleWeekDrafts: false,
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    isTakeoffMode() {
      return true
    },

    isTakeoffRootView() {
      return this.parentAssignmentId == null
    },

    isReschedulePlanStep() {
      return this.rescheduleMode && this.isTakeoffRootView && this.reschedulePlanStep === 2
    },

    rescheduleWeekNumbers() {
      const start = Number(this.weekNumber)
      const base = Number.isFinite(start) && start > 0 ? start : 1
      return [0, 1, 2, 3, 4, 5].map((offset) => base + offset)
    },

    tableColspan() {
      if (this.isReschedulePlanStep) {
        return 3 + this.rescheduleWeekNumbers.length + 1
      }
      // expand + Slot,TAG,Etapa,Cant prog/ejec,Ud,Peso,%Avance,Índice HH,HH prog/reg,acciones
      return this.rescheduleMode ? 4 : 13
    },

    activeRescheduleSelectedIds() {
      return this.rescheduleSelectedIds != null
        ? this.rescheduleSelectedIds
        : this.localRescheduleSelectedIds
    },

    rescheduleSelectedCount() {
      return this.activeRescheduleSelectedIds.length
    },

    hasRescheduleWeekDraftChanges() {
      if (!this.isReschedulePlanStep) {
        return false
      }
      return (this.confirmedRescheduleAssignments || []).some((assignment) => (
        this.isAssignmentWeekDraftDirty(assignment)
      ))
    },

    canSaveRescheduleWeekDrafts() {
      return this.hasRescheduleWeekDraftChanges && !this.savingRescheduleWeekDrafts
    },

    showSectionHeader() {
      return this.parentAssignmentId == null
    },

    takeoffFilterDefs() {
      return [
        { code: 'area', labelKey: 'area' },
        { code: 'engineering_doc', labelKey: 'engineering_doc' },
        { code: 'planner', labelKey: 'planner' },
      ]
    },

    /** Filtros a aplicar en esta tabla (root propios o heredados). */
    effectiveTakeoffFilters() {
      if (this.isTakeoffRootView) {
        return this.takeoffFilters
      }
      return this.takeoffActiveFilters
    },

    takeoffFiltersForChildren() {
      return this.effectiveTakeoffFilters
    },

    hasActiveTakeoffFilters() {
      const filters = this.effectiveTakeoffFilters
      if (!filters) {
        return false
      }
      return this.takeoffFilterDefs.some((def) => (filters[def.code] || '').trim() !== '')
    },

    displayRows() {
      if (this.isReschedulePlanStep) {
        return this.confirmedRescheduleAssignments
      }

      let rows = this.assignments

      if (this.isTakeoffRootView) {
        rows = rows.filter((assignment) => {
          if (assignment.variant_type == null || assignment.variant_type === '') {
            return true
          }
          return TAKEOFF_VARIANT_TYPES.includes(Number(assignment.variant_type))
        })
      }

      if (!this.hasActiveTakeoffFilters) {
        return rows
      }

      // Root: padre visible si coincide o si está desplegado y algún hijo coincide.
      // Hijos (tabla anidada): solo los que coinciden.
      if (this.isTakeoffRootView) {
        return rows.filter((assignment, assignmentIndex) => {
          if (this.assignmentMatchesTakeoffFilters(assignment)) {
            return true
          }
          return this.hasMatchingExpandedChild(assignment, assignmentIndex)
        })
      }

      return rows.filter((assignment) => this.assignmentMatchesTakeoffFilters(assignment))
    },

    displayCount() {
      if (this.isTakeoffRootView) {
        return this.displayRows.length
      }
      if (this.count > 0) {
        return this.count
      }
      return this.displayRows.length
    },

    effectiveProjectId() {
      return this.projectId || (this.project && this.project.id) || null
    },
  },

  watch: {
    activityId() {
      this.reloadAssignments()
    },
    parentAssignmentId() {
      this.reloadAssignments()
    },
    selectedDisciplineId() {
      this.reloadAssignments()
    },
    rescheduleMode(active) {
      if (!active) {
        this.resetReschedulePlanState()
        if (this.rescheduleSelectedIds == null) {
          this.localRescheduleSelectedIds = []
        }
      }
    },
    isReschedulePlanStep(active) {
      if (active) {
        this.emitRescheduleWeekDraftsPreview()
        return
      }
      this.emitRescheduleWeekDraftsPreviewClear()
    },
    confirmedRescheduleAssignments: {
      deep: true,
      handler() {
        if (this.isReschedulePlanStep) {
          this.emitRescheduleWeekDraftsPreview()
        }
      },
    },
    takeoffFilterQueries: {
      deep: true,
      handler(queries) {
        Object.keys(queries || {}).forEach((code) => {
          if (!(queries[code] || '').trim() && this.takeoffFilters[code]) {
            this.$set(this.takeoffFilters, code, '')
          }
        })
      },
    },
  },

  mounted() {
    this.reloadAssignments()
    this._onWeeklyAssignmentsSilentRefresh = () => this.scheduleSilentReload()
    EventBus.$on('weeklyAssignmentsSilentRefresh', this._onWeeklyAssignmentsSilentRefresh)
  },

  beforeDestroy() {
    if (this.silentRefreshTimer) {
      clearTimeout(this.silentRefreshTimer)
      this.silentRefreshTimer = null
    }
    EventBus.$off('weeklyAssignmentsSilentRefresh', this._onWeeklyAssignmentsSilentRefresh)
    this.emitRescheduleWeekDraftsPreviewClear()
  },

  methods: {
    emitRescheduleWeekDraftsPreviewClear() {
      EventBus.$emit('takeoffRescheduleWeekDraftsPreview', { clear: true })
    },

    /**
     * Delta por stage y semana (borrador actual − valor guardado) para la preview del header.
     * El back ya devuelve la suma de las distribuciones guardadas; el front solo suma el cambio.
     */
    emitRescheduleWeekDraftsPreview() {
      if (!this.isTakeoffRootView || !this.isReschedulePlanStep) {
        this.emitRescheduleWeekDraftsPreviewClear()
        return
      }

      const deltaByStage = {}
      ;(this.confirmedRescheduleAssignments || []).forEach((assignment) => {
        const stageId = assignment && (assignment.boq_root_id
          || (assignment.boq_root && assignment.boq_root.id))
        if (stageId == null) {
          return
        }
        if (!deltaByStage[stageId]) {
          deltaByStage[stageId] = {}
        }
        this.rescheduleWeekNumbers.forEach((weekNum) => {
          const current = this.normalizeWeekDraftValue(
            assignment.weekDrafts && assignment.weekDrafts[weekNum],
          )
          const saved = this.normalizeWeekDraftValue(
            assignment.savedWeekDrafts && assignment.savedWeekDrafts[weekNum],
          )
          deltaByStage[stageId][weekNum] = (deltaByStage[stageId][weekNum] || 0)
            + (current - saved)
        })
      })

      EventBus.$emit('takeoffRescheduleWeekDraftsPreview', {
        baseWeek: Number(this.weekNumber) || null,
        weeks: this.rescheduleWeekNumbers.slice(),
        deltaByStage,
      })
    },

    getAssignmentFilterValue(assignment, filterCode) {
      const fieldables = assignment && (assignment.filter_fieldables || assignment.filters)
      if (!Array.isArray(fieldables)) {
        return ''
      }
      const match = fieldables.find((item) => {
        const code = (item.filter && item.filter.code) || item.code || null
        return code === filterCode
      })
      return match && match.value_free != null ? String(match.value_free).trim() : ''
    },

    /**
     * Extrae value_free únicos por código de filtro desde una lista de assignments.
     */
    extractTakeoffFilterOptions(assignments) {
      const map = {
        area: [],
        engineering_doc: [],
        planner: [],
      }
      if (!Array.isArray(assignments)) {
        return map
      }

      const seen = {
        area: new Set(),
        engineering_doc: new Set(),
        planner: new Set(),
      }

      assignments.forEach((assignment) => {
        this.takeoffFilterDefs.forEach((def) => {
          const value = this.getAssignmentFilterValue(assignment, def.code)
          if (!value) {
            return
          }
          const key = value.toLowerCase()
          if (seen[def.code].has(key)) {
            return
          }
          seen[def.code].add(key)
          map[def.code].push(value)
        })
      })

      return map
    },

    /**
     * Fusiona opciones nuevas en la lista precargada del root (sin duplicados).
     */
    mergeTakeoffFilterOptions(incoming) {
      if (!incoming || typeof incoming !== 'object') {
        return
      }

      this.takeoffFilterDefs.forEach((def) => {
        const current = this.takeoffFilterOptions[def.code] || []
        const seen = new Set(current.map((value) => value.toLowerCase()))
        const next = [...current]
        const values = Array.isArray(incoming[def.code]) ? incoming[def.code] : []

        values.forEach((raw) => {
          const value = raw == null ? '' : String(raw).trim()
          if (!value) {
            return
          }
          const key = value.toLowerCase()
          if (seen.has(key)) {
            return
          }
          seen.add(key)
          next.push(value)
        })

        next.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        this.$set(this.takeoffFilterOptions, def.code, next)
      })
    },

    /**
     * Tras cargar assignments: actualiza lista de autocomplete (root) o notifica al padre (hijo).
     */
    processAssignmentsForTakeoffFilters(assignments) {
      const extracted = this.extractTakeoffFilterOptions(assignments)
      if (this.isTakeoffRootView) {
        this.mergeTakeoffFilterOptions(extracted)
        return
      }
      this.$emit('takeoff-filter-values', extracted)
    },

    filteredTakeoffOptions(filterCode) {
      const query = (this.takeoffFilterQueries[filterCode] || '').trim().toLowerCase()
      const options = this.takeoffFilterOptions[filterCode] || []
      if (!query) {
        return options
      }
      return options.filter((option) => option.toLowerCase().includes(query))
    },

    onTakeoffFilterTyping(filterCode, value) {
      const next = value == null ? '' : String(value)
      this.$set(this.takeoffFilterQueries, filterCode, next)
      if (!next || next !== this.takeoffFilters[filterCode]) {
        this.$set(this.takeoffFilters, filterCode, '')
      }
    },

    onTakeoffFilterSelect(filterCode, option) {
      const value = option == null ? '' : String(option)
      this.$set(this.takeoffFilters, filterCode, value)
      this.$set(this.takeoffFilterQueries, filterCode, value)
    },

    assignmentMatchesTakeoffFilters(assignment) {
      const filters = this.effectiveTakeoffFilters
      if (!filters) {
        return true
      }
      return this.takeoffFilterDefs.every((def) => {
        const selected = (filters[def.code] || '').trim().toLowerCase()
        if (!selected) {
          return true
        }
        return this.getAssignmentFilterValue(assignment, def.code).toLowerCase() === selected
      })
    },

    hasMatchingExpandedChild(assignment, assignmentIndex) {
      if (!this.isAssignmentExpanded(assignment, assignmentIndex)) {
        return false
      }
      const children = assignment.child
      if (!Array.isArray(children) || !children.length) {
        return false
      }
      return children.some((child) => this.assignmentMatchesTakeoffFilters(child))
    },

    scheduleSilentReload() {
      if (this.silentRefreshTimer) {
        clearTimeout(this.silentRefreshTimer)
      }
      this.silentRefreshTimer = setTimeout(() => {
        this.silentRefreshTimer = null
        this.silentReloadAssignments()
      }, 150)
    },

    async silentReloadAssignments() {
      if (!this.activityId) {
        return
      }
      await this.reloadAssignments({ silent: true })
      await Promise.all(
        this.assignments.map((assignment, assignmentIndex) => {
          if (!this.isAssignmentExpanded(assignment, assignmentIndex)) {
            return Promise.resolve()
          }
          if (!this.assignmentHasChildren(assignment)) {
            return Promise.resolve()
          }
          return this.loadAssignments(this.activityId, assignment.id, assignment, { silent: true })
        })
      )
    },

    reloadAssignments(options = {}) {
      if (!this.activityId) {
        return Promise.resolve()
      }
      this.assignmentsLoadToken += 1
      const token = this.assignmentsLoadToken
      this.paginationAfterIds = null
      this.hasMoreAssignments = false

      if (this.parentAssignmentId != null) {
        return this.loadAssignments(this.activityId, this.parentAssignmentId, null, options)
      }

      return this.loadAssignments(this.activityId, null, null, { ...options, token })
        .then(() => this.loadRemainingAssignments(token, options))
    },

    /** Encadena los lotes restantes hasta agotar la paginación (lazy loading). */
    loadRemainingAssignments(token, options = {}) {
      if (token !== this.assignmentsLoadToken || !this.hasMoreAssignments) {
        return Promise.resolve()
      }
      return this.loadAssignments(this.activityId, null, null, {
        ...options,
        append: true,
        token,
      }).then(() => this.loadRemainingAssignments(token, options))
    },

    loadAssignments(id, parentAssignmentId = null, row = null, options = {}) {
      const silent = options.silent === true
      const append = options.append === true
      const token = options.token != null ? options.token : null
      if (!silent && !append) {
        this.loadingData = true
      }
      if (append && !silent) {
        this.loadingMore = true
      }
      const url = `/api/v2/activity/${id}/assignments`
      const params = {
        with_boq_root: 1,
        with_filters: 1,
        with_weekly_distributions: 1,
      }
      if (parentAssignmentId) {
        params.parent_assignment_id = parentAssignmentId
      }
      if (this.selectedDisciplineId != null) {
        params.takeoff_discipline_id = this.selectedDisciplineId
      }
      // Lazy loading: solo la lista raíz se pagina; los hijos de una fila son pocos.
      const isPaginatedList = !parentAssignmentId && !row
      if (isPaginatedList) {
        params.per_page = ASSIGNMENTS_PAGE_SIZE
        if (append && this.paginationAfterIds != null) {
          params.after_id = this.paginationAfterIds
        }
      }
      const isStale = () => token != null && token !== this.assignmentsLoadToken

      return axios.get(url, { params })
        .then((response) => {
          if (isStale()) {
            return
          }
          if (response.data && response.data.success) {
            const loaded = response.data.data || []
            const pagination = response.data.pagination || null
            if (row) {
              this.$set(row, 'child', loaded)
            } else if (append) {
              this.assignments = this.assignments.concat(loaded)
            } else {
              this.assignments = loaded
              if (this.isTakeoffRootView) {
                this.takeoffFilterOptions = {
                  area: [],
                  engineering_doc: [],
                  planner: [],
                }
              }
            }
            this.processAssignmentsForTakeoffFilters(loaded)

            // Solo la lista raíz mantiene el cursor: las cargas de hijos no traen
            // `pagination` y no deben cortar la cadena de lotes en curso.
            if (isPaginatedList) {
              this.hasMoreAssignments = !!(pagination && pagination.has_more)
              this.paginationAfterIds = pagination ? pagination.next_after_id : null
            }
          }
        })
        .catch(() => {
          // silent refresh keeps previous data visible; corta la cadena de lotes
          if (isPaginatedList) {
            this.hasMoreAssignments = false
          }
        })
        .finally(() => {
          if (!silent && !append) {
            this.loadingData = false
          }
          if (append && !silent) {
            this.loadingMore = false
          }
          if (!row && !isStale()) {
            this.$nextTick(() => {
              this.restoreAssignmentsFromSession()
            })
          }
        })
    },

    persistAssignmentsSession() {
      const projectId = this.effectiveProjectId
      if (!projectId || !this.activityId) {
        return
      }

      const state = readWeeklyUiSession(projectId)
      const activityPrefix = `${this.activityId}:`
      const levelPrefix = `${this.parentAssignmentId || 'root'}-`

      const kept = state.assignments.filter((storedKey) => {
        if (!String(storedKey).startsWith(activityPrefix)) {
          return true
        }
        return !String(storedKey).slice(activityPrefix.length).startsWith(levelPrefix)
      })

      this.expandedAssignmentKeys.forEach((localKey) => {
        kept.push(`${activityPrefix}${localKey}`)
      })

      state.assignments = kept
      writeWeeklyUiSession(projectId, state)
    },

    async restoreAssignmentsFromSession() {
      const projectId = this.effectiveProjectId
      if (!projectId || !this.activityId || !this.assignments.length) {
        return
      }

      const state = readWeeklyUiSession(projectId)
      const activityPrefix = `${this.activityId}:`
      const levelPrefix = `${this.parentAssignmentId || 'root'}-`

      this.expandedAssignmentKeys = state.assignments
        .filter((storedKey) => {
          if (!String(storedKey).startsWith(activityPrefix)) {
            return false
          }
          return String(storedKey).slice(activityPrefix.length).startsWith(levelPrefix)
        })
        .map((storedKey) => String(storedKey).slice(activityPrefix.length))

      await Promise.all(
        this.assignments.map((assignment, assignmentIndex) => {
          if (!this.isAssignmentExpanded(assignment, assignmentIndex)) {
            return Promise.resolve()
          }
          if (!this.assignmentHasChildren(assignment)) {
            return Promise.resolve()
          }
          // Se invoca tras cada lote de la carga progresiva: no repetir los ya resueltos.
          if (Array.isArray(assignment.child)) {
            return Promise.resolve()
          }
          return this.loadAssignments(this.activityId, assignment.id, assignment, { silent: true })
        })
      )
    },

    assignmentRowKey(assignment, assignmentIndex) {
      return assignment.id != null ? assignment.id : assignmentIndex
    },

    assignmentExpandKey(assignment, assignmentIndex) {
      return `${this.parentAssignmentId || 'root'}-${this.assignmentRowKey(assignment, assignmentIndex)}`
    },

    assignmentHasChildren(assignment) {
      return Number(assignment.children_count || 0) > 0
    },

    isAssignmentExpanded(assignment, assignmentIndex) {
      return this.expandedAssignmentKeys.includes(
        this.assignmentExpandKey(assignment, assignmentIndex)
      )
    },

    toggleAssignmentDetail(assignment, assignmentIndex) {
      const key = this.assignmentExpandKey(assignment, assignmentIndex)
      if (this.isAssignmentExpanded(assignment, assignmentIndex)) {
        this.expandedAssignmentKeys = this.expandedAssignmentKeys.filter((k) => k !== key)
        this.persistAssignmentsSession()
        return
      }
      this.expandedAssignmentKeys = [...this.expandedAssignmentKeys, key]
      this.loadAssignments(this.activityId, assignment.id, assignment).then(() => {
        this.persistAssignmentsSession()
      })
    },

    assignmentTypeLabel(assignment) {
      if (!assignment.type) {
        return this.$t('assignment')
      }
      if (this.parentAssignmentId != null && assignment.type === 'resource') {
        return this.$t('sub_assignment')
      }
      if (assignment.type === 'resource') {
        return this.$t('assignment')
      }
      return this.$t(assignment.type)
    },

    hasBoqRootSource(assignment) {
      return assignment.boq_root_source === 'imported' || assignment.boq_root_source === 'manual'
    },

    isImportedBoqSource(assignment) {
      return assignment.boq_root_source === 'imported'
    },

    assignmentSlotIcon(assignment) {
      return this.isImportedBoqSource(assignment) ? 'calendar-alt' : 'ruler-combined'
    },

    assignmentSlotIconClass(assignment) {
      return this.isImportedBoqSource(assignment)
        ? 'weekly-activity-assignments-table__slot-icon--imported'
        : 'weekly-activity-assignments-table__slot-icon--manual'
    },

    assignmentSlotBadgeClass(assignment) {
      if (!this.hasBoqRootSource(assignment)) {
        return ''
      }
      return this.isImportedBoqSource(assignment)
        ? 'weekly-activity-assignments-table__slot-badge--imported'
        : 'weekly-activity-assignments-table__slot-badge--manual'
    },

    assignmentDescription(assignment) {
      return assignment.description || assignment.label || assignment.name || '—'
    },

    assignmentStageName(assignment) {
      const root = assignment && assignment.boq_root
      if (!root) {
        return '—'
      }
      return root.name || root.code || '—'
    },

    assignmentPending(assignment) {
      const total = parseFloat(this.assignmentTotal(assignment))
      const executed = parseFloat(this.assignmentExecutedQuantity(assignment))
      if (Number.isNaN(total)) {
        return null
      }
      if (Number.isNaN(executed)) {
        return total
      }
      return Math.max(0, total - executed)
    },

    assignmentSupervisor(assignment) {
      const user = assignment && assignment.user
      if (!user) {
        return '—'
      }
      if (user.name) {
        return user.name
      }
      if (user.full_name) {
        return user.full_name
      }
      const parts = [user.first_name, user.last_name].filter(Boolean)
      return parts.length ? parts.join(' ') : '—'
    },

    assignmentUnit(assignment) {
      return assignment.unit_name || assignment.unit || '—'
    },

    assignmentTotal(assignment) {
      if (assignment.type === 'task') {
        return assignment.weight
      }
      if (assignment.total != null && assignment.total !== '') {
        return assignment.total
      }
      if (assignment.total_quantity != null && assignment.total_quantity !== '') {
        return assignment.total_quantity
      }
      return assignment.scheduled_quantity
    },

    /**
     * Cant. ejecutada:
     * - hijo type5/type6: suma de líneas OK (unidades).
     * - padre type5: igual al progreso % (total 100; no suma unidades de hijos).
     * - padre type6: unidades ponderadas Σ(units × weight% × progress%);
     *   el progress del padre sigue siendo %; esta columna muestra unidades.
     */
    assignmentExecutedQuantity(assignment) {
      if (!assignment) {
        return null
      }
      const variant = Number(assignment.variant_type)
      if (variant === VARIANT_TYPE_TAKEOFF || variant === VARIANT_TYPE_TAKEOFF_TYPE2) {
        if (this.assignmentHasChildren(assignment)) {
          if (variant === VARIANT_TYPE_TAKEOFF_TYPE2) {
            return this.takeoffType6ParentExecutedUnits(assignment)
          }
          return this.assignmentProgress(assignment)
        }
        if (Array.isArray(assignment.lines)) {
          return this.sumOkLineUnits(assignment)
        }
      }
      if (assignment.executed_quantity == null || assignment.executed_quantity === '') {
        return null
      }
      return assignment.executed_quantity
    },

    /**
     * Padre type6: Σ(hijo.total × hijo.weight% × hijo.progress%).
     * Preferimos executed_quantity del API si ya viene calculado.
     */
    takeoffType6ParentExecutedUnits(assignment) {
      if (assignment.executed_quantity != null && assignment.executed_quantity !== '') {
        const fromApi = parseFloat(assignment.executed_quantity)
        if (!Number.isNaN(fromApi)) {
          return fromApi
        }
      }
      const children = Array.isArray(assignment.child)
        ? assignment.child
        : (Array.isArray(assignment.children) ? assignment.children : [])
      let total = 0
      children.forEach((child) => {
        const units = parseFloat(child.total != null ? child.total : child.total_quantity) || 0
        const weight = parseFloat(child.weight) || 0
        const percent = parseFloat(this.assignmentProgress(child)) || 0
        total += units * (weight / 100) * (percent / 100)
      })
      return roundDecimal(total)
    },

    sumOkLineUnits(assignment) {
      const lines = assignment && Array.isArray(assignment.lines) ? assignment.lines : []
      let total = 0
      lines.forEach((line) => {
        if (Number(line && line.status) !== ASSIGNMENT_LINE_STATUS_OK) {
          return
        }
        const qty = parseFloat(line.executed_quantity)
        if (!Number.isNaN(qty)) {
          total += qty
        }
      })
      return total
    },

    assignmentProgress(assignment) {
      if (assignment.type === 'task') {
        return parseFloat(assignment.executed_quantity) || 0
      }
      // type5/type6: el % lo da progress. No se deriva de las unidades porque una medición
      // con cantidad 0 también puede tener avance, y el padre lo pondera por weight.
      if (
        Number(assignment.variant_type) === VARIANT_TYPE_TAKEOFF
        || Number(assignment.variant_type) === VARIANT_TYPE_TAKEOFF_TYPE2
      ) {
        return parseFloat(assignment.progress) || 0
      }
      const total = parseFloat(assignment.total)
      if (!total || total <= 0) {
        return parseFloat(assignment.progress) || 0
      }
      return (parseFloat(assignment.executed_quantity) * 100) / total
    },

    formatWeightPercent(assignment) {
      const weight = formatDecimal(assignment && assignment.weight)
      return weight != null ? `${weight}%` : '—'
    },

    formatProgressPercent(assignment) {
      const progress = formatDecimal(this.assignmentProgress(assignment))
      return progress != null ? `${progress}%` : '—'
    },

    assignmentManHourIndex(assignment) {
      const index = parseFloat(assignment && assignment.man_hour_index)
      if (Number.isNaN(index)) {
        return null
      }
      return index
    },

    assignmentAdjustedManHourIndex(assignment) {
      const index = parseFloat(
        assignment && assignment.boq_root && assignment.boq_root.adjusted_man_hour_index
      )
      if (Number.isNaN(index)) {
        return null
      }
      return index
    },

    isTakeoffType2Variant(assignment) {
      return Number(assignment && assignment.variant_type) === VARIANT_TYPE_TAKEOFF_TYPE2
    },

    /**
     * HH programadas:
     * - type5 (y resto): cantidad programada × índice HH
     * - type6: (unidades × peso%) × índice HH ajustado del stage (boq_root)
     */
    assignmentScheduledHours(assignment) {
      if (this.isTakeoffType2Variant(assignment)) {
        const index = this.assignmentAdjustedManHourIndex(assignment)
        const units = parseFloat(this.assignmentTotal(assignment))
        const weight = parseFloat(assignment && assignment.weight)
        if (index == null || Number.isNaN(units) || Number.isNaN(weight)) {
          return null
        }
        return roundDecimal(units * (weight / 100) * index)
      }

      const total = parseFloat(this.assignmentTotal(assignment))
      const index = this.assignmentManHourIndex(assignment)
      if (Number.isNaN(total) || index == null) {
        return null
      }
      return roundDecimal(total * index)
    },

    /**
     * HH registradas:
     * - type5 (y resto): índice HH ajustado del stage × suma líneas OK
     * - type6: HH programadas × progress% de avance
     */
    assignmentRegisteredHours(assignment) {
      if (this.isTakeoffType2Variant(assignment)) {
        const scheduled = this.assignmentScheduledHours(assignment)
        if (scheduled == null) {
          return null
        }
        const progress = parseFloat(this.assignmentProgress(assignment))
        if (Number.isNaN(progress)) {
          return null
        }
        return roundDecimal(scheduled * (progress / 100))
      }

      const adjustedIndex = this.assignmentAdjustedManHourIndex(assignment)
      if (adjustedIndex == null) {
        return null
      }

      let executedOk = 0
      let hasOkLines = false
      const lines = assignment && Array.isArray(assignment.lines) ? assignment.lines : []
      lines.forEach((line) => {
        if (Number(line && line.status) !== ASSIGNMENT_LINE_STATUS_OK) {
          return
        }
        const qty = parseFloat(line.executed_quantity)
        if (Number.isNaN(qty)) {
          return
        }
        hasOkLines = true
        executedOk += qty
      })

      if (!hasOkLines) {
        return null
      }

      return roundDecimal(adjustedIndex * executedOk)
    },

    canSelectForReschedule(assignment) {
      return this.rescheduleMode && assignment && !this.assignmentHasChildren(assignment)
    },

    isRescheduleSelected(assignment) {
      if (!assignment || assignment.id == null) {
        return false
      }
      return this.activeRescheduleSelectedIds.some((id) => id === assignment.id)
    },

    toggleRescheduleSelection(assignment) {
      if (!this.canSelectForReschedule(assignment)) {
        return
      }
      this.onToggleRescheduleAssignment(assignment.id)
    },

    onToggleRescheduleAssignment(assignmentId) {
      if (this.rescheduleSelectedIds != null) {
        this.$emit('toggle-reschedule-assignment', assignmentId)
        return
      }
      const next = this.localRescheduleSelectedIds.slice()
      const index = next.indexOf(assignmentId)
      if (index >= 0) {
        next.splice(index, 1)
      } else {
        next.push(assignmentId)
      }
      this.localRescheduleSelectedIds = next
    },

    confirmRescheduleSelection() {
      const ids = this.activeRescheduleSelectedIds.slice()
      if (!ids.length) {
        return
      }

      const byId = {}
      this.flattenAssignmentTree(this.assignments).forEach((assignment) => {
        if (assignment && assignment.id != null) {
          byId[assignment.id] = assignment
        }
      })

      this.confirmedRescheduleAssignments = ids
        .map((id) => byId[id])
        .filter(Boolean)
        .map((assignment) => {
          const drafts = this.buildWeekDraftsFromAssignment(assignment)
          return {
            ...assignment,
            weekDrafts: { ...drafts },
            savedWeekDrafts: { ...drafts },
          }
        })
      this.reschedulePlanStep = 2
      this.$emit('confirm-reschedule-selection', ids)
    },

    backToRescheduleSelection() {
      this.reschedulePlanStep = 1
      this.confirmedRescheduleAssignments = []
      this.savingRescheduleWeekDrafts = false
      this.emitRescheduleWeekDraftsPreviewClear()
    },

    resetReschedulePlanState() {
      this.reschedulePlanStep = 1
      this.confirmedRescheduleAssignments = []
      this.savingRescheduleWeekDrafts = false
      this.emitRescheduleWeekDraftsPreviewClear()
    },

    buildEmptyWeekDrafts() {
      const drafts = {}
      this.rescheduleWeekNumbers.forEach((weekNum) => {
        drafts[weekNum] = 0
      })
      return drafts
    },

    buildWeekDraftsFromAssignment(assignment) {
      const drafts = this.buildEmptyWeekDrafts()
      const rows = Array.isArray(assignment && assignment.weekly_distributions)
        ? assignment.weekly_distributions
        : []
      rows.forEach((row) => {
        const weekNum = Number(row && row.week_number)
        if (!Object.prototype.hasOwnProperty.call(drafts, weekNum)) {
          return
        }
        const qty = parseFloat(row.scheduled_quantity)
        drafts[weekNum] = Number.isFinite(qty) && qty > 0 ? qty : 0
      })
      return drafts
    },

    normalizeWeekDraftValue(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }
      const num = Number(value)
      if (!Number.isFinite(num) || num < 0) {
        return 0
      }
      return num
    },

    isAssignmentWeekDraftDirty(assignment) {
      if (!assignment || !assignment.weekDrafts) {
        return false
      }
      const saved = assignment.savedWeekDrafts || {}
      return this.rescheduleWeekNumbers.some((weekNum) => (
        this.normalizeWeekDraftValue(assignment.weekDrafts[weekNum])
          !== this.normalizeWeekDraftValue(saved[weekNum])
      ))
    },

    weekDatesForNumber(weekNum) {
      const base = Number(this.weekNumber)
      const start = moment(this.weekStartDate, 'YYYY-MM-DD', true)
      const end = moment(this.weekEndDate, 'YYYY-MM-DD', true)
      if (!Number.isFinite(base) || !start.isValid() || !end.isValid()) {
        return null
      }
      const offset = Number(weekNum) - base
      return {
        start: start.clone().add(offset * 7, 'days').format('YYYY-MM-DD'),
        end: end.clone().add(offset * 7, 'days').format('YYYY-MM-DD'),
      }
    },

    buildWeeksPayload(assignment) {
      const weeks = []
      this.rescheduleWeekNumbers.forEach((weekNum) => {
        const dates = this.weekDatesForNumber(weekNum)
        if (!dates) {
          return
        }
        const quantity = this.normalizeWeekDraftValue(
          assignment.weekDrafts && assignment.weekDrafts[weekNum],
        )
        weeks.push({
          week_number: weekNum,
          starts_on: dates.start,
          ends_on: dates.end,
          scheduled_quantity: quantity > 0 ? quantity : null,
        })
      })
      return weeks
    },

    async saveRescheduleWeekDrafts() {
      if (!this.canSaveRescheduleWeekDrafts || !this.effectiveProjectId) {
        return
      }

      const dirty = (this.confirmedRescheduleAssignments || []).filter((assignment) => (
        this.isAssignmentWeekDraftDirty(assignment)
      ))
      if (!dirty.length) {
        return
      }

      const missingDates = dirty.some((assignment) => (
        this.buildWeeksPayload(assignment).length !== this.rescheduleWeekNumbers.length
      ))
      if (missingDates) {
        this.$notify.error('error_saving')
        return
      }

      this.savingRescheduleWeekDrafts = true
      try {
        await Promise.all(dirty.map(async (assignment) => {
          const { data } = await axios.put(
            `/api/v2/project/${this.effectiveProjectId}/assignments/${assignment.id}/weekly-distributions`,
            { weeks: this.buildWeeksPayload(assignment) },
          )
          if (!(data && data.success)) {
            throw new Error('save_failed')
          }
          const list = Array.isArray(data.weekly_distributions) ? data.weekly_distributions : []
          const nextDrafts = this.buildEmptyWeekDrafts()
          list.forEach((row) => {
            const weekNum = Number(row && row.week_number)
            if (!Object.prototype.hasOwnProperty.call(nextDrafts, weekNum)) {
              return
            }
            const qty = parseFloat(row.scheduled_quantity)
            nextDrafts[weekNum] = Number.isFinite(qty) && qty > 0 ? qty : 0
          })
          this.$set(assignment, 'weekDrafts', { ...nextDrafts })
          this.$set(assignment, 'savedWeekDrafts', { ...nextDrafts })
          this.$set(assignment, 'weekly_distributions', list)
        }))
        this.$notify.success('save_successful')
        this.emitRescheduleWeekDraftsPreview()
        EventBus.$emit('takeoffRescheduleWeekDraftsSaved')
      } catch (error) {
        this.$notify.error('error_saving')
      } finally {
        this.savingRescheduleWeekDrafts = false
      }
    },

    flattenAssignmentTree(rows) {
      const out = []
      const walk = (list) => {
        ;(list || []).forEach((row) => {
          out.push(row)
          if (Array.isArray(row.child) && row.child.length) {
            walk(row.child)
          }
        })
      }
      walk(rows)
      return out
    },

    isEmpty(value) {
      return value === null || value === undefined || value === ''
    },

    formatQuantity(value) {
      const formatted = formatDecimal(value)
      return formatted != null ? formatted : value
    },

    canRegisterProgress(assignment) {
      if (!assignment || assignment.completed) {
        return false
      }
      if (assignment.type && assignment.type !== 'resource') {
        return false
      }
      return Number(assignment.children_count || 0) === 0
    },

    onProgressRegistered() {
      this.cancelTakeoffProgressEdit()
      this.silentReloadAssignments()
    },

    onRegisterProgressClick(assignment) {
      if (!this.canRegisterProgress(assignment)) {
        return
      }
      this.startTakeoffProgressEdit(assignment)
    },

    isTakeoffProgressEditing(assignment) {
      return !!(
        assignment
        && this.takeoffProgressEdit.assignmentId != null
        && this.takeoffProgressEdit.assignmentId === assignment.id
      )
    },

    startTakeoffProgressEdit(assignment) {
      const current = Number(this.assignmentProgress(assignment))
      const percent = Number.isNaN(current) ? 0 : roundDecimal(current)
      this.takeoffProgressEdit = {
        assignmentId: assignment.id,
        percent,
        originalPercent: percent,
        saving: false,
      }
    },

    cancelTakeoffProgressEdit() {
      this.takeoffProgressEdit = {
        assignmentId: null,
        percent: null,
        originalPercent: null,
        saving: false,
      }
    },

    /**
     * Convierte el % objetivo a unidades de avance (parcial) y registra igual que el modal.
     */
    async saveTakeoffProgressEdit(assignment) {
      if (!assignment || !this.activityId || this.takeoffProgressEdit.saving) {
        return
      }

      const total = parseFloat(assignment.total)
      if (!total || total <= 0 || Number.isNaN(total)) {
        this.$notify.error('assignment_update_error')
        return
      }

      let targetPercent = parseFloat(this.takeoffProgressEdit.percent)
      if (Number.isNaN(targetPercent)) {
        this.$notify.error('please_complete_required_fields')
        return
      }
      targetPercent = Math.min(100, Math.max(0, targetPercent))

      const originalPercent = parseFloat(this.takeoffProgressEdit.originalPercent) || 0
      if (Math.abs(targetPercent - originalPercent) < 0.00001) {
        this.cancelTakeoffProgressEdit()
        return
      }

      const currentExecuted = parseFloat(this.assignmentExecutedQuantity(assignment)) || 0
      const targetExecuted = (targetPercent / 100) * total
      const increment = Number((targetExecuted - currentExecuted).toFixed(5))

      if (increment <= 0) {
        this.$notify.error('please_complete_required_fields')
        return
      }

      const today = moment().format('YYYY-MM-DD')
      const payload = {
        executed_quantity: increment,
        start_date: today,
        end_date: today,
        observation: null,
        executed_units: increment,
        executed_length: null,
        executed_width: null,
        executed_height: null,
        is_actual_progress: false,
        people: 1,
      }

      this.takeoffProgressEdit.saving = true
      try {
        const { data } = await axios.post(
          `/api/v2/assignments/activity/${this.activityId}/${assignment.id}`,
          payload
        )
        if (data && data.success) {
          this.$notify.success('assignment_update_success')
          EventBus.$emit('refreshAssignmentChangedNotifications', {
            activityId: this.activityId,
            assignmentId: assignment.id,
          })
          EventBus.$emit('refreshActivitiesNotifications', true)
          this.onProgressRegistered()
          return
        }
        this.$notify.error((data && data.msg) || 'assignment_update_error')
      } catch (error) {
        let message = this.$t('assignment_update_error')
        if (error.response && error.response.status === 422 && error.response.data.errors) {
          const errors = error.response.data.errors
          const firstKey = Object.keys(errors)[0]
          if (firstKey && errors[firstKey] && errors[firstKey][0]) {
            message = errors[firstKey][0]
          }
        }
        this.$notify.error(message)
      } finally {
        if (this.takeoffProgressEdit) {
          this.takeoffProgressEdit.saving = false
        }
      }
    },
  },
}
</script>

<style scoped>
.weekly-activity-assignments-table__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #e8f5ec;
  border-bottom: 1px solid #b8dcc4;
}

.weekly-activity-assignments-table__level-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  background-color: #2d6a3e;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
}

.weekly-activity-assignments-table__count {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #2d6a3e;
}

.weekly-activity-assignments-table__body {
  min-height: 0;
  padding: 0.75rem;
}

.weekly-activity-assignments-table__section {
  border: 1px solid #d7e2de;
  border-radius: 0.375rem;
  overflow: hidden;
  background-color: #fff;
}

.weekly-activity-assignments-table__section--nested {
  border: none;
  border-radius: 0;
}

.weekly-activity-assignments-table__section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(90deg, #1f6b5c 0%, #3d8b5a 100%);
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.weekly-activity-assignments-table__section-icon {
  flex-shrink: 0;
}

.weekly-activity-assignments-table__section-title {
  flex: 1;
  min-width: 0;
}

.weekly-activity-assignments-table__header {
  padding: 0.375rem 0.5rem;
  text-align: center;
  font-weight: 600;
  color: #fff;
  background-color: #3d8b5a;
  border: 1px solid #3d8b5a;
  white-space: nowrap;
}

.weekly-activity-assignments-table__header--left {
  text-align: left;
}

.weekly-activity-assignments-table__expand-col,
.weekly-activity-assignments-table__actions-col {
  min-width: 2.5rem;
  width: 2.5rem;
}

.weekly-activity-assignments-table__cell {
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  color: #1f2937;
  vertical-align: middle;
  background-color: #fff;
}

.weekly-activity-assignments-table__slot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}

.weekly-activity-assignments-table__slot-icon--imported {
  color: #7c3aed;
}

.weekly-activity-assignments-table__slot-icon--manual {
  color: #2d6a3e;
}

.weekly-activity-assignments-table__slot-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background-color: #2d6a3e;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
}

.weekly-activity-assignments-table__slot-badge--imported {
  background-color: #7c3aed;
}

.weekly-activity-assignments-table__slot-badge--manual {
  background-color: #2d6a3e;
}

.weekly-activity-assignments-table__row--imported > .weekly-activity-assignments-table__expand-col {
  box-shadow: inset 3px 0 0 #7c3aed;
}

.weekly-activity-assignments-table__code-link {
  color: #2d6a3e;
  font-weight: 600;
  text-decoration: none;
}

.weekly-activity-assignments-table__code-link:hover {
  text-decoration: underline;
}

.weekly-activity-assignments-table__name-cell {
  max-width: 14rem;
}

.weekly-activity-assignments-table__name-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.weekly-activity-assignments-table__progress-cell {
  min-width: 8.5rem;
  width: 8.5rem;
}

.weekly-activity-assignments-table__progress-edit {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  width: 100%;
}

.weekly-activity-assignments-table__progress-input {
  width: 3.5rem;
  min-width: 0;
  padding: 0.15rem 0.25rem;
  border: 1px solid #c5cdd8;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  text-align: right;
}

.weekly-activity-assignments-table__progress-suffix {
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 600;
}

.weekly-activity-assignments-table__progress-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  cursor: pointer;
}

.weekly-activity-assignments-table__progress-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.weekly-activity-assignments-table__progress-action--save {
  color: #2d6a3e;
}

.weekly-activity-assignments-table__progress-action--save:hover:not(:disabled) {
  background: #e8f5ec;
}

.weekly-activity-assignments-table__progress-action--cancel {
  color: #b91c1c;
}

.weekly-activity-assignments-table__progress-action--cancel:hover:not(:disabled) {
  background: #fef2f2;
}

.weekly-activity-assignments-table__complete-icon--yes {
  color: #38c172;
}

.weekly-activity-assignments-table__complete-icon--no {
  color: #e3342f;
}

.weekly-activity-assignments-table__empty {
  color: #c4c4c4;
}

.weekly-activity-assignments-table__row--expanded > .weekly-activity-assignments-table__cell {
  background-color: #e8f5ec;
}

.weekly-activity-assignments-table__detail-cell {
  border: none !important;
  padding: 0 !important;
}

.weekly-activity-assignments-table__detail-panel {
  border-left: 3px solid #3d8b5a;
  background-color: #f4f7f6;
}

.weekly-activity-assignments-table__section-header--takeoff {
  background: linear-gradient(90deg, #1f6b5c 0%, #3d8b5a 100%);
  color: #fff;
}

.weekly-activity-assignments-table__section-header--takeoff .weekly-activity-assignments-table__section-icon,
.weekly-activity-assignments-table__section-header--takeoff .weekly-activity-assignments-table__section-title {
  color: #fff;
}

.weekly-activity-assignments-table__takeoff-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  align-items: flex-end;
  padding: 0.65rem 0.85rem;
  background: #f4f7f6;
  border-bottom: 1px solid #d7e2de;
}

.weekly-activity-assignments-table__takeoff-filter {
  min-width: 10rem;
  flex: 1 1 10rem;
  max-width: 16rem;
}

.weekly-activity-assignments-table__takeoff-filter-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #1f6b5c;
}

.weekly-activity-assignments-table__takeoff-autocomplete {
  width: 100%;
}

.weekly-activity-assignments-table__section-badge {
  margin-left: auto;
  padding: 0.1rem 0.45rem;
  border-radius: 0.2rem;
  background: rgba(255, 255, 255, 0.18);
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.weekly-activity-assignments-table__filter-count {
  margin-left: auto;
  align-self: flex-end;
  padding-bottom: 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #1f6b5c;
}

.weekly-activity-assignments-table__reschedule-step {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  padding: 0.55rem 0.85rem;
  background: #f7f3ea;
  border-bottom: 1px solid #e6dcc8;
}

.weekly-activity-assignments-table__reschedule-step-text {
  font-size: 0.75rem;
  color: #5b4a32;
}

.weekly-activity-assignments-table__reschedule-confirm {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border: none;
  border-radius: 0.25rem;
  background: #b45309;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}

.weekly-activity-assignments-table__reschedule-confirm:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.weekly-activity-assignments-table__reschedule-back {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid #c5d4ce;
  border-radius: 0.25rem;
  background: #fff;
  color: #1f6b5c;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}

.weekly-activity-assignments-table__header--week {
  min-width: 4.5rem;
  background: #0f766e !important;
  color: #fff !important;
  border-color: #0d9488 !important;
}

.weekly-activity-assignments-table__week-cell {
  background: #f0fdf9;
  min-width: 4.5rem;
}

.weekly-activity-assignments-table__week-input {
  width: 4rem;
  padding: 0.2rem 0.3rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.35rem;
  text-align: right;
  font-size: 0.75rem;
  background: #fff;
}

.weekly-activity-assignments-table__grid-wrap--compact {
  display: inline-block;
  max-width: 100%;
  overflow-x: auto;
  vertical-align: top;
}

.weekly-activity-assignments-table__grid--compact {
  width: max-content;
  max-width: 100%;
}

.weekly-activity-assignments-table__check-col {
  min-width: 2.25rem;
  width: 2.25rem;
}

.weekly-activity-assignments-table__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.weekly-activity-assignments-table__check input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.weekly-activity-assignments-table__check-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  border: 1.5px solid #9ca3af;
  border-radius: 0.2rem;
  background: #fff;
  color: #fff;
}

.weekly-activity-assignments-table__check-mark--on {
  border-color: #b45309;
  background: #b45309;
}

.weekly-activity-assignments-table__check-mark--done:not(.weekly-activity-assignments-table__check-mark--on) {
  border-color: #1f6b5c;
  background: #1f6b5c;
  border-radius: 9999px;
}

.weekly-activity-assignments-table__tag-cell {
  min-width: 14rem;
}

.weekly-activity-assignments-table__tag-main {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
}

.weekly-activity-assignments-table__tag-icon {
  margin-top: 0.15rem;
  color: #1f6b5c;
}

.weekly-activity-assignments-table__tag-texts {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
}

.weekly-activity-assignments-table__tag-sub {
  font-size: 0.6875rem;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 16rem;
}

.weekly-activity-assignments-table__weight-value,
.weekly-activity-assignments-table__progress-value {
  color: #1f6b5c;
  font-weight: 700;
}

.weekly-activity-assignments-table__row--selected > .weekly-activity-assignments-table__cell {
  background: #fff7ed;
}

.weekly-activity-assignments-table--reschedule .weekly-activity-assignments-table__section {
  display: inline-block;
  max-width: 100%;
  vertical-align: top;
}
</style>

<style>
body > .autocomplete.control,
body > .datepicker.control,
body > div > .dropdown .dropdown-menu {
  z-index: 120 !important;
}
</style>
